import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import RepeatIcon from '@mui/icons-material/Repeat';
import { Badge, Divider, IconButton, MenuItem } from '@mui/material';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import useMessageUsersScheduledSessions, { ScheduledTask } from '../../../../hooks/useMessageUsersScheduledSessions';
import StyledLocalMenu from '../../../common/StyledLocalMenu';
import MessageUsersScheduledTaskChangeSettingsDialog from './MessageUsersScheduledTaskChangeSettingsDialog';

const MessageUsersScheduledListRowLocalMenu = ({ scheduledTask }: { scheduledTask: ScheduledTask }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const sortMenuOpen = Boolean(anchorEl);

    const dialog = useDialog();
    const { enqueueSnackbar } = useSnackbar();

    const {
        isFetching,
        deleteScheduledMessage,
        isUpdatingScheduledMessage,
        setScheduleMessageInactiveStatus: setScheduleMessageActiveStatus,
    } = useMessageUsersScheduledSessions();

    const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDeleteScheduledMessage = (id: string) => {
        dialog
            .confirm({
                message: 'Are you sure you want to delete this scheduled message?',
                ok: { text: 'Delete', variant: 'contained', color: 'error' },
                cancel: { text: 'Cancel' },
                title: 'Delete Scheduled Message',
            })
            .then(async () => {
                const result = await deleteScheduledMessage(id);

                if (result && result.success) {
                    enqueueSnackbar('Scheduled message deleted.', { variant: 'success' });
                } else {
                    enqueueSnackbar('Failed to delete scheduled message.', { variant: 'error' });
                }
            })
            .catch(() => {});
    };

    return (
        <>
            <IconButton disabled={isUpdatingScheduledMessage || isFetching} onClick={handleMenuClick}>
                <Badge
                    variant="standard"
                    badgeContent={<RepeatIcon sx={{ fontSize: 14, p: 0 }} />}
                    invisible={!(scheduledTask.repeatEveryAmount && scheduledTask.repeatEveryUnit)}
                    sx={{ p: 0 }}
                >
                    <MoreVertIcon />
                </Badge>
            </IconButton>
            <StyledLocalMenu
                id="menu"
                MenuListProps={{
                    'aria-labelledby': 'button',
                }}
                anchorEl={anchorEl}
                open={sortMenuOpen}
                onClose={handleMenuClose}
            >
                <MenuItem
                    color={scheduledTask.inactive ? 'default' : 'success'}
                    onClick={() => {
                        setScheduleMessageActiveStatus(scheduledTask._id!, !scheduledTask.inactive)
                            .then(() => {
                                enqueueSnackbar(`Scheduled message ${scheduledTask.inactive ? 'activated' : 'deactivated'}.`, {
                                    variant: 'success',
                                });
                            })
                            .catch(() => {
                                enqueueSnackbar('Failed to update scheduled message status.', {
                                    variant: 'error',
                                });
                            });
                        handleMenuClose();
                    }}
                    disableRipple
                >
                    <PowerSettingsNewIcon />
                    {scheduledTask.inactive ? 'Set Active' : 'Set Inactive'}
                </MenuItem>
                <Divider />
                <MenuItem
                    color="primary"
                    onClick={() => {
                        setOpenEditDialog(true);
                        handleMenuClose();
                    }}
                    disableRipple
                >
                    <EditIcon />
                    Edit Settings
                </MenuItem>
                <Divider />
                <MenuItem
                    onClick={() => {
                        handleDeleteScheduledMessage(scheduledTask._id!);
                        handleMenuClose();
                    }}
                    disableRipple
                >
                    <DeleteIcon color="error" />
                    Delete Scheduled Message
                </MenuItem>
            </StyledLocalMenu>
            <MessageUsersScheduledTaskChangeSettingsDialog
                open={openEditDialog}
                onClose={() => setOpenEditDialog(false)}
                scheduledTask={scheduledTask}
            />
        </>
    );
};

export default MessageUsersScheduledListRowLocalMenu;
